import { Flex, Link, Progress } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';

import { resolveAgentforgeHref } from '../../../helpers/agentforgeHref';
import type {
  BulkOperationBlockData,
  BulkOperationStatus,
} from '../../../types';
import { Card, StatusPill } from '../primitives';
import { useAgentForgeBulkActivity } from './useAgentForgeBulkActivity';

interface BulkOperationBlockProps {
  data: BulkOperationBlockData;
}

const terminalTitle = (
  activity: string,
  status: BulkOperationStatus,
  fallback: string
): string => {
  if (status === 'pending' || status === 'in_progress') return fallback;
  const failed = status === 'failed';
  switch (activity) {
    case 'add_to_job':
      return failed
        ? 'Could not add candidates to job'
        : 'Candidates added to job';
    case 'reactivate_application':
      return failed
        ? 'Could not reactivate applications'
        : 'Applications reactivated';
    case 'ai_coordinator_dispatch':
      return failed
        ? 'Could not coordinate interviews'
        : 'Interview coordination queued';
    default:
      return failed ? 'Bulk operation failed' : 'Bulk operation completed';
  }
};

const successVerb = (activity: string): string => {
  switch (activity) {
    case 'add_to_job':
      return 'added';
    case 'reactivate_application':
      return 'reactivated';
    case 'ai_coordinator_dispatch':
      return 'scheduled';
    default:
      return 'succeeded';
  }
};

export const BulkOperationBlock = ({ data }: BulkOperationBlockProps) => {
  const { data: live, isUnavailable } = useAgentForgeBulkActivity(
    data.activityId
  );

  const status = live?.status ?? data.status;
  const progress = live?.progress ?? data.progress;
  const total = progress.total || data.total;
  const processed = Math.min(
    total,
    Math.max(
      progress.processed,
      progress.success + progress.failed + progress.skipped
    )
  );
  const terminal = status === 'completed' || status === 'failed';
  const hasIssues =
    status === 'failed' || progress.failed > 0 || progress.skipped > 0;
  const percent = terminal
    ? 100
    : progress.percentage ||
      (total > 0 ? Math.round((processed / total) * 100) : 0);

  const targetName =
    live?.target?.name ||
    live?.ats_job?.name ||
    live?.job_name ||
    data.target?.name;
  const targetUrl = live?.target?.url || live?.ats_job?.url || data.target?.url;

  const statusLabel =
    isUnavailable && !live
      ? 'Status unavailable'
      : status === 'pending'
        ? 'Queued'
        : status === 'in_progress'
          ? 'In progress'
          : status === 'failed'
            ? 'Failed'
            : hasIssues
              ? 'Completed with issues'
              : 'Completed';
  const statusColor =
    isUnavailable && !live
      ? 'secondary-400'
      : status === 'failed'
        ? 'error-500'
        : status === 'completed' && !hasIssues
          ? 'success-500'
          : status === 'completed'
            ? 'warning-500'
            : status === 'in_progress'
              ? 'primary-500'
              : 'secondary-400';

  const inferredSuccess =
    status === 'completed' && !hasIssues && progress.success === 0
      ? total
      : progress.success;
  const inferredFailed =
    status === 'failed' &&
    progress.success === 0 &&
    progress.failed === 0 &&
    progress.skipped === 0
      ? total
      : progress.failed;
  const terminalCounts = [
    inferredSuccess > 0
      ? `${inferredSuccess} ${successVerb(data.activity)}`
      : '',
    progress.skipped > 0 ? `${progress.skipped} skipped` : '',
    inferredFailed > 0 ? `${inferredFailed} failed` : '',
  ].filter(Boolean);
  const countSummary =
    isUnavailable && !live
      ? 'Open Recent activities to check the latest result'
      : terminal
        ? terminalCounts.join(' · ') || 'No items completed'
        : status === 'pending'
          ? `${total} ${total === 1 ? 'item' : 'items'} waiting to start`
          : `${processed} of ${total} processed`;

  return (
    <Card
      interactive={false}
      maxW="560px"
      data-test-id={`agentforge-bulk-operation-${data.activityId}`}
    >
      <Flex direction="column" gap="10px">
        <Flex align="flex-start" justify="space-between" gap="12px">
          <Flex direction="column" minW={0} gap="2px">
            <Text variant="label" className="text-secondary-900">
              {isUnavailable && !live
                ? 'Bulk operation submitted'
                : terminalTitle(data.activity, status, data.title)}
            </Text>
            {targetName && (
              <Text variant="small" className="text-secondary-500" truncate>
                {targetName}
              </Text>
            )}
          </Flex>
          <StatusPill label={statusLabel} color={statusColor} />
        </Flex>

        {!isUnavailable && (
          <Progress
            value={percent}
            isIndeterminate={status === 'in_progress' && percent === 0}
            size="xs"
            borderRadius="full"
            colorScheme={
              status === 'failed'
                ? 'red'
                : status === 'completed' && !hasIssues
                  ? 'green'
                  : 'primary'
            }
            aria-label="Bulk operation progress"
          />
        )}

        <Flex align="center" justify="space-between" gap="12px">
          <Text variant="small" className="text-secondary-600">
            {countSummary}
          </Text>
          <Flex gap="10px" flexShrink={0}>
            {targetUrl && (
              <Link
                href={resolveAgentforgeHref(targetUrl)}
                fontSize="12px"
                color="primary-500"
                fontWeight="500"
              >
                Open {data.target?.type === 'job' ? 'pipeline' : 'details'}
              </Link>
            )}
            {terminal && hasIssues && (
              <Link
                href="/notifications/recent-activities"
                fontSize="12px"
                color="primary-500"
                fontWeight="500"
              >
                Review details
              </Link>
            )}
            {isUnavailable && !live && (
              <Link
                href="/notifications/recent-activities"
                fontSize="12px"
                color="primary-500"
                fontWeight="500"
              >
                Check recent activities
              </Link>
            )}
          </Flex>
        </Flex>

        {!terminal && !isUnavailable && (
          <Text variant="small" className="text-secondary-400">
            Updates automatically — you can keep working.
          </Text>
        )}
      </Flex>
    </Card>
  );
};
